import React from 'react';
import { Inline } from '@atlaskit/primitives';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIssueHierarchyLevel } from '@atlassian/jira-issue-context-service/src/context.tsx';
import { SectionHeadingTitle } from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import {
	useProjectKey,
	useProjectType,
	useIsSimplifiedProject,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import InlineHeaderMessageInfoPanel from '../inline-header-message-info-panel/index.tsx';
import messages from './messages.tsx';

type TitleProps = {
	title?: MessageDescriptor;
	/** Updated Title for former 'Issues in this epic' for Epic renaming beta */
	titleChildIssues?: MessageDescriptor;
	hasMultipleChildPanels?: boolean;
};

const testId = 'issue.views.common.child-issues-panel.heading.title';

const getDefaultTitle = () => {
	if (expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
		return messages.titleIssueTerminologyRefreshM4;
	}
	return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? messages.titleIssueTermRefresh
		: messages.title;
};

export const Title = ({
	title = getDefaultTitle(),
	titleChildIssues,
	hasMultipleChildPanels = false,
}: TitleProps) => {
	const { formatMessage } = useIntl();

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const isSimplified = useIsSimplifiedProject(projectKey);
	const [issueHierarchyLevel] = useIssueHierarchyLevel();
	const isIssueBaseLevel = issueHierarchyLevel !== undefined && issueHierarchyLevel === 0;

	return (
		<Inline>
			<SectionHeadingTitle data-testid={testId}>
				{projectType === 'software' && !isSimplified && titleChildIssues
					? formatMessage(titleChildIssues)
					: formatMessage(title, { issueLevel: isIssueBaseLevel ? 'subtask' : 'work' })}
			</SectionHeadingTitle>
			{hasMultipleChildPanels && fg('jira-issue-view-child-issues-panel-merge-subtasks') ? (
				<InlineHeaderMessageInfoPanel />
			) : null}
		</Inline>
	);
};
